import React, { useEffect, useRef, useState } from "react";
import { Text, PaymentDropdown } from "components/common";
import { PAYMENT_METHODS_QUERY, UPDATE_PAYMENT_METHOD_INFO } from "utils";
import { useMutation } from "@apollo/react-hooks";
import BankInfoForm from "components/common/ui/BankInfoForm";
import { SUCCESS_STATUS } from "utils/constants";

const VerifyPaymentMethodInfo = ({
  paymentMethods = [],
  setPaymentMethods = () => {},
  onAllUpdated = () => {},
}) => {
  const [routingNumber, setRoutingNumber] = useState(null);
  const [accountNumber, setAccountNumber] = useState(null);
  const [confirmAccountNumber, setConfirmAccountNumber] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    paymentMethods[0],
  );
  const [updatePaymentMethodInfo, { loading }] = useMutation(
    UPDATE_PAYMENT_METHOD_INFO,
  );
  const [error, setError] = useState("");
  const [accountUpdateSuccess, setAccountUpdateSuccess] = useState(false);

  useEffect(() => {
    if (accountUpdateSuccess) {
      setTimeout(() => {
        setAccountUpdateSuccess(false);
      }, 5000);
    }
  }, [accountUpdateSuccess]);

  const bankInfoRef = useRef(null);

  const bankInfoProps = {
    routingNumber,
    setRoutingNumber,
    accountNumber,
    setAccountNumber,
    confirmAccountNumber,
    setConfirmAccountNumber,
    required: true,
  };

  const resetFormOnChange = (newData) => {
    setSelectedPaymentMethod(newData);
    bankInfoRef.current.resetFields();
    setError("");
  };

  const handleSelectedAccountUpdate = (data = {}) => {
    resetFormOnChange(data);
  };

  const updatePaymentMethod = async () => {
    const args = {
      id: selectedPaymentMethod.id,
      accountNumber,
      routingNumber,
    };

    const res = await updatePaymentMethodInfo({
      variables: args,
      update: (cache, { data: response }) => {
        if (response?.updatePaymentMethodInfo?.status === SUCCESS_STATUS) {
          const data = cache.readQuery({
            query: PAYMENT_METHODS_QUERY,
          });
          cache.writeData({
            data: {
              user: {
                __typename: "User",
                payment_methods: data.user.payment_methods.map((pm) => {
                  if (pm.id === selectedPaymentMethod.id) {
                    return {
                      __typename: "UserPaymentMethod",
                      ...pm,
                      manually_updated_on: Date.now().toString(),
                      needs_account_routing_numbers: false,
                    };
                  }
                  return { __typename: "UserPaymentMethod", ...pm };
                }),
              },
            },
          });
        }
      },
    });
    const { status } = res?.data?.updatePaymentMethodInfo || null;

    if (status !== SUCCESS_STATUS) {
      setError("Unable to validate banking information, please try again.");
      return;
    }

    setAccountUpdateSuccess(true);

    // remove that payment method from this list
    const indexToRemove = paymentMethods.findIndex(
      (pm) => pm.id === selectedPaymentMethod.id,
    );
    if (indexToRemove > -1) {
      // indexToRemove should be valid but just in case
      paymentMethods.splice(indexToRemove, 1);
    }

    setPaymentMethods(paymentMethods);

    if (paymentMethods.length === 0) {
      onAllUpdated();
      return;
    }
    resetFormOnChange(paymentMethods[0]);
  };

  const onUpdateAccount = () => {
    bankInfoRef.current.resetFieldErrors();
    setError("");
    const isValid = bankInfoRef.current.validateFields();

    if (isValid) {
      updatePaymentMethod();
    }
  };

  return (
    <div className="flex flex-col p-4 text-center sm:p-10">
      <div className="flex-1 h-full align-center pt-5">
        <hr />
        <Text className="mb-2 pt-3" size="text-2xl">
          Verify Bank Information
        </Text>
        <span className="mb-3">
          Update your banking info to avoid service interruptions and seamlessly
          build credit.
        </span>

        <Text className="text-base mt-3 mb-1">Payment Method</Text>
        <PaymentDropdown
          paymentMethods={paymentMethods}
          defaultElementId={selectedPaymentMethod?.id}
          selectedElement={selectedPaymentMethod}
          didSelect={handleSelectedAccountUpdate}
          dropDownClassName="Dropdown-paymentDrawer"
        />
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <BankInfoForm ref={bankInfoRef} {...bankInfoProps} />

        <Text className="text-base text-red-600 mt-3 mb-1">{error}</Text>
        <button
          type="button"
          className="btn btn-green mt-3 px-10 py-2 w-full"
          onClick={onUpdateAccount}
          disabled={loading}
        >
          Update Account
        </button>

        {accountUpdateSuccess && (
          <div className="flex flex-col p-3 text-center">
            <div className="duration-500 ease-in-out">
              <div className="w-full mb-4 text-xl font-bold text-gray-primary">
                Account Update Successful.
              </div>
            </div>
            <div className="w-full mb-6 text-2xl">
              Please update remaining accounts as well.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyPaymentMethodInfo;
